import type { BaseComponent } from '../types/protocols';

/**
 * A component node is well-formed when it carries the `key` and `type` that
 * every client writes when it creates a component.
 *
 * Anything else is a fragment: a single field that landed under a component
 * which was never fully saved or had already been deleted (seen in production
 * as `{ outdoorCategory: 'structures' }`), or a node a client wrote to
 * `components/undefined` after building a component from such a fragment.
 *
 * Fragments must be skipped, not rendered as "undefined" — and never written
 * to, because a component built from one has no key to write under.
 */
export function isWellFormedComponent(data: unknown): data is BaseComponent {
    if (!data || typeof data !== 'object') return false;
    const { key, type } = data as { key?: unknown; type?: unknown };
    return typeof key === 'string' && key.length > 0
        && typeof type === 'string' && type.length > 0;
}

/**
 * The well-formed components of a `structure.components` map, in map order.
 * Fragments are dropped and reported once per call so they can be cleaned up.
 */
export function wellFormedComponents(
    components: { [key: string]: BaseComponent } | null | undefined
): BaseComponent[] {
    if (!components) return [];

    const result: BaseComponent[] = [];
    const skipped: string[] = [];

    Object.entries(components).forEach(([mapKey, data]) => {
        if (isWellFormedComponent(data)) {
            result.push(data);
        } else {
            skipped.push(mapKey);
        }
    });

    if (skipped.length > 0) {
        console.warn('Skipping malformed component nodes (no key or type):', skipped);
    }

    return result;
}
